import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { ProgrammeBankAccountSection } from '@dealstack/frontend/app/pages/Project/MIP/components/ProgrammeBankAccount/ProgrammeBankAccountSection';
import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Controller, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import {
	GENERAL_DEAL_INPUTS_FORM,
	useGeneralDealInputsController,
} from './useGeneralDealInputsController';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { TextInput, Select } from 'react-hook-form-mantine';
import { CURRENCY_OPTIONS } from '@dealstack/shared/lib/format/currency/currency.ts';
import { Flex, Text } from '@mantine/core';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { ProgrammeDefaultsBanner } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/ProgrammeDefaultsBanner/ProgrammeDefaultsBanner';

interface Props {
	className?: string;
}

const SECTION_WIDTH = '27rem';

const GeneralDealInputs = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const {
		generalDealInputsForm,
		isLoading,
		onSubmit,
		bankAccountDetails,
		entityOptions,
		onCreateNewEntity,
		disableCurrencySelect,
	} = useGeneralDealInputsController();

	return (
		<div className={className}>
			{renderMIPCreateSectionHeaderPortal(
				<SectionHeader
					title={tMipCreate('general_deal_inputs.header.title')}
					subtitle={tMipCreate('general_deal_inputs.header.subtitle')}
				/>
			)}
			{isLoading ? <InlineLoader /> : null}
			<ProgrammeDefaultsBanner dealId={dealId} mipId={mipId} />
			<FormProvider {...generalDealInputsForm}>
				<form onSubmit={generalDealInputsForm.handleSubmit(onSubmit)}>
					<SectionHeader
						title={tMipCreate('general_deal_inputs.sections.about_the_offer')}
						size="h5b"
					/>
					<Flex direction="column" gap="1rem" mb="2rem">
						<TextInput
							data-testid={
								TEST_ELEMENT_IDS.MIP_CREATE_GENERAL_DEAL_INPUTS_PROGRAMME_NAME_INPUT
							}
							label={tMipCreate('general_deal_inputs.form.programme_name_label')}
							name={GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME}
							style={{ width: SECTION_WIDTH }}
							size="md"
						/>
						<Controller
							name={GENERAL_DEAL_INPUTS_FORM.ENTITY_ID}
							control={generalDealInputsForm.control}
							render={({ field }) => (
								<CreatableSelect
									value={field.value ?? null}
									onChange={field.onChange}
									data-testid={
										TEST_ELEMENT_IDS.MIP_CREATE_GENERAL_DEAL_INPUTS_ENTITY_SELECT
									}
									label={tMipCreate('general_deal_inputs.form.entity_label')}
									size="md"
									w={SECTION_WIDTH}
									shouldCreate={(query, data) => {
										if (!query) {
											return false;
										}
										const entity = data.find(
											(entity) => entity.label === query
										);
										return !entity;
									}}
									onCreate={onCreateNewEntity}
									getCreateLabel={(query) => (
										<Flex
											align="center"
											gap="0.5rem"
											data-testid={
												TEST_ELEMENT_IDS.MIP_CREATE_GENERAL_DEAL_INPUTS_ENTITY_CREATE_NEW
											}
										>
											<Icon variant="add" />
											<Text>
												{tMipCreate(
													'general_deal_inputs.form.create_new_entity'
												)}
											</Text>
											<Text>"{query}"</Text>
										</Flex>
									)}
									data={entityOptions}
								/>
							)}
						/>

						<Select
							name={GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY}
							data-testid={TEST_ELEMENT_IDS.CURRENCY_OF_SECURITY}
							label={tMipCreate('general_deal_inputs.form.currency_label')}
							searchable
							size="md"
							data={CURRENCY_OPTIONS}
							style={{ width: SECTION_WIDTH }}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							disabled={disableCurrencySelect}
						/>
					</Flex>
					<SectionHeader
						title={tMipCreate('general_deal_inputs.sections.payment_instructions')}
						tooltip={tMipCreate(
							'general_deal_inputs.sections.payment_instructions_tooltip'
						)}
						size="h5b"
					/>
					<Flex maw="28rem" mb="2rem">
						<ProgrammeBankAccountSection
							bankAccount={bankAccountDetails}
							instructionsFieldName={GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS}
							width={SECTION_WIDTH}
							gap="1rem"
						/>
					</Flex>
				</form>
			</FormProvider>
		</div>
	);
};

const Styled = styled(GeneralDealInputs)`
	margin-bottom: 2rem;
	position: relative;

	${SectionHeader} {
		margin-bottom: 0.8rem;
	}
`;

export { Styled as GeneralDealInputs };
